// Small shared component that renders the org's primary logo (or
// name fallback) for chrome surfaces. Two variants:
//
//   * ``topbar`` — fixed 32 px height, alt-text from org name, used
//     by the worklist and worksheet topbars.
//   * ``login``  — larger (64 px) and centred, with the org name as a
//     subtitle beneath. Uses the unauthenticated ``/organisation/public``
//     endpoint instead of the authenticated query.
//
// Render rules per spec: logo if present, otherwise org name as text,
// otherwise nothing (no broken-image icon, no placeholder).

import { useOrganisation, useOrganisationPublic } from "@/api/organisation-api";

interface OrgBrandProps {
  variant: "topbar" | "login";
}

export function OrgBrand({ variant }: OrgBrandProps) {
  if (variant === "login") return <LoginBrand />;
  return <TopbarBrand />;
}

function TopbarBrand() {
  const query = useOrganisation();
  const org = query.data;
  if (!org) return null;
  if (org.primary_logo_url) {
    return (
      <img
        data-cy="topbar-org-logo"
        src={`${org.primary_logo_url}?v=${encodeURIComponent(org.updated_at)}`}
        alt={org.name || "Organisation logo"}
        className="h-8 w-auto"
      />
    );
  }
  if (org.name) {
    return (
      <span
        data-cy="topbar-org-name"
        className="text-sm font-semibold text-foreground-on-chrome"
      >
        {org.name}
      </span>
    );
  }
  return null;
}

function LoginBrand() {
  const query = useOrganisationPublic();
  const org = query.data;
  if (!org) return null;
  return (
    <div className="flex flex-col items-center gap-2" data-cy="login-org-brand">
      {org.primary_logo_url ? (
        <img
          data-cy="login-org-logo"
          src={org.primary_logo_url}
          alt={org.name || "Organisation logo"}
          className="h-16 w-auto"
        />
      ) : null}
      {org.name ? (
        <p
          data-cy="login-org-name"
          className="text-sm text-foreground-muted"
        >
          {org.name}
        </p>
      ) : null}
    </div>
  );
}
